"use strict";
/**
 * LanyardService.ts
 * Integrates with Lanyard (https://api.lanyard.rest/) to pull live Discord status,
 * activities (games, Visual Studio Code, YouTube, etc.), Spotify rich presence,
 * and custom status for Meci profiles in real-time via WebSocket and REST fallback.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.lanyardService = void 0;
class LanyardService {
    constructor() {
        this.ws = null;
        this.heartbeatTimer = null;
        this.reconnectTimer = null;
        this.restPollTimer = null;
        this.listeners = new Set();
        this.currentData = null;
        this.isConnecting = false;
        if (typeof window !== 'undefined') {
            const savedId = this.getDiscordId();
            if (savedId && this.isEnabled()) {
                this.connect(savedId);
            }
        }
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    notify() {
        this.listeners.forEach((l) => {
            try {
                l();
            }
            catch { }
        });
    }
    isEnabled() {
        if (typeof window === 'undefined')
            return false;
        return localStorage.getItem('meci_lanyard_enabled') === 'true';
    }
    setEnabled(enabled) {
        if (typeof window === 'undefined')
            return;
        localStorage.setItem('meci_lanyard_enabled', String(enabled));
        if (enabled) {
            const id = this.getDiscordId();
            if (id)
                this.connect(id);
        }
        else {
            this.disconnect();
        }
        this.notify();
    }
    getDiscordId() {
        if (typeof window === 'undefined')
            return '';
        return localStorage.getItem('meci_lanyard_discord_id') || '';
    }
    setDiscordId(discordId) {
        if (typeof window === 'undefined')
            return;
        const cleanId = discordId.trim().replace(/[^0-9]/g, '');
        localStorage.setItem('meci_lanyard_discord_id', cleanId);
        if (cleanId && this.isEnabled()) {
            this.connect(cleanId);
        }
        else {
            this.disconnect();
        }
        this.notify();
    }
    isShowOnProfile() {
        if (typeof window === 'undefined')
            return true;
        return localStorage.getItem('meci_lanyard_show_profile') !== 'false';
    }
    setShowOnProfile(val) {
        if (typeof window === 'undefined')
            return;
        localStorage.setItem('meci_lanyard_show_profile', String(val));
        this.notify();
    }
    isSyncStatus() {
        if (typeof window === 'undefined')
            return true;
        return localStorage.getItem('meci_lanyard_sync_status') !== 'false';
    }
    setSyncStatus(val) {
        if (typeof window === 'undefined')
            return;
        localStorage.setItem('meci_lanyard_sync_status', String(val));
        this.notify();
    }
    getData() {
        return this.currentData;
    }
    /**
     * Connect via WebSocket to Lanyard for instant sub-second presence updates
     */
    connect(discordId) {
        this.cleanupSockets();
        const cleanId = discordId.trim().replace(/[^0-9]/g, '');
        if (!cleanId)
            return;
        // Fetch initial REST data immediately
        this.fetchUserREST(cleanId).catch(() => { });
        try {
            this.isConnecting = true;
            const wsUrl = 'wss://api.lanyard.rest/socket';
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = () => {
                this.isConnecting = false;
            };
            this.ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    const { op, d, t } = message;
                    // op 1 = Hello (provides heartbeat interval)
                    if (op === 1 && d?.heartbeat_interval) {
                        this.startHeartbeat(d.heartbeat_interval);
                        // Send op 2 (Initialize subscription to user)
                        this.ws?.send(JSON.stringify({
                            op: 2,
                            d: {
                                subscribe_to_id: cleanId,
                            },
                        }));
                    }
                    // op 0 = Event dispatch
                    if (op === 0) {
                        if (t === 'INIT_STATE' || t === 'PRESENCE_UPDATE') {
                            this.currentData = d;
                            localStorage.setItem('meci_lanyard_cached_data', JSON.stringify(d));
                            this.notify();
                        }
                    }
                }
                catch (err) {
                    console.warn('[Lanyard] Message parse error:', err);
                }
            };
            this.ws.onerror = (err) => {
                console.warn('[Lanyard] WebSocket error:', err);
            };
            this.ws.onclose = () => {
                this.cleanupSockets();
                // Fallback to REST polling if WebSocket disconnects
                if (this.isEnabled()) {
                    this.reconnectTimer = setTimeout(() => {
                        const currentId = this.getDiscordId();
                        if (currentId && this.isEnabled()) {
                            this.connect(currentId);
                        }
                    }, 8000);
                }
            };
        }
        catch (e) {
            console.warn('[Lanyard] Failed to open WebSocket:', e);
            this.startRESTPolling(cleanId);
        }
    }
    startHeartbeat(intervalMs) {
        if (this.heartbeatTimer)
            clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({ op: 3 }));
            }
        }, intervalMs);
    }
    /**
     * Fetch one-off user presence via Lanyard REST API
     */
    async fetchUserREST(discordId) {
        const cleanId = discordId.trim().replace(/[^0-9]/g, '');
        if (!cleanId)
            return null;
        try {
            const res = await fetch(`https://api.lanyard.rest/v1/users/${cleanId}`);
            if (!res.ok) {
                throw new Error(`Lanyard API error: ${res.status}`);
            }
            const json = await res.json();
            if (json && json.success && json.data) {
                this.currentData = json.data;
                localStorage.setItem('meci_lanyard_cached_data', JSON.stringify(json.data));
                this.notify();
                return json.data;
            }
        }
        catch (e) {
            console.warn('[Lanyard] REST fetch failed:', e);
        }
        return null;
    }
    startRESTPolling(discordId) {
        if (this.restPollTimer)
            clearInterval(this.restPollTimer);
        this.fetchUserREST(discordId);
        this.restPollTimer = setInterval(() => {
            if (this.isEnabled()) {
                this.fetchUserREST(discordId);
            }
        }, 15000);
    }
    disconnect() {
        this.cleanupSockets();
        this.currentData = null;
        this.notify();
    }
    cleanupSockets() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.restPollTimer) {
            clearInterval(this.restPollTimer);
            this.restPollTimer = null;
        }
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onmessage = null;
            try {
                this.ws.close();
            }
            catch { }
            this.ws = null;
        }
    }
    /**
     * Helper to resolve Discord / Lanyard activity asset URLs
     */
    resolveAssetUrl(activity, assetKey) {
        if (!assetKey)
            return '';
        if (assetKey.startsWith('mp:external/')) {
            const clean = assetKey.replace('mp:external/', '');
            return `https://media.discordapp.net/external/${clean}`;
        }
        if (assetKey.startsWith('http://') || assetKey.startsWith('https://')) {
            return assetKey;
        }
        if (activity?.application_id) {
            return `https://cdn.discordapp.com/app-assets/${activity.application_id}/${assetKey}.png`;
        }
        return '';
    }
    /**
     * Helper to get avatar URL from discord_user
     */
    getAvatarUrl(user) {
        if (!user || !user.avatar)
            return '';
        const isGif = user.avatar.startsWith('a_');
        const ext = isGif ? 'gif' : 'png';
        return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.${ext}?size=256`;
    }
}
exports.lanyardService = new LanyardService();
